import React from 'react';

import { ADJUST_URL } from 'helpers/constants';
import { useLocale } from 'client/i18n/hooks/useLocale';
import styled from '@emotion/styled';
import { PlatformBadge } from '../../PlatformBadge';
import serverRenderingUtils from '../../../../helpers/serverRenderingUtils';
import {
  GOOGLE_LOCALE_MAPPING,
  APPLE_LOCALE_MAPPING,
} from '../../../../helpers/localeMappings';
import { AppStoreDownloadButton } from '../../../screens/QuicksilverAppScreen/components/AppStoreDownloadButton';
import { localeCodeMapping } from '../constants';

const MOBILE_APP_STORE_URL = `${ADJUST_URL}/t1g5iu3?redirect=https%3A%2F%2Fanchor.fm%2Fapp`;
const MOBILE_PLAY_STORE_URL = `${ADJUST_URL}/qohxi8g?redirect=https%3A%2F%2Fanchor.fm%2Fapp`;

const AppButtonContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const DesktopContainer = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-gap: 20px;
  justify-content: space-between;

  @media (max-width: 400px) {
    grid-auto-flow: row;
  }
`;

export const MarketingAppButtons = ({
  clickEventLocation,
  clickEventSection,
  isRenderForMobileOnly,
  className,
}: {
  clickEventLocation: string;
  clickEventSection?: string;
  isRenderForMobileOnly?: boolean;
  className?: string;
}) => {
  const { locale } = useLocale();

  const supportedGoogleMobileLanguages = [
    'pt-BR',
    'es-LA',
    'es-ES',
    'de-DE',
    'fr',
    'sv',
    'id',
    'it',
    'ja',
    'ko',
    'hi',
    'nl',
    'pl',
    'tr',
    'fr-CA',
  ];

  const supportedAppleMobileLanguages = [
    ...supportedGoogleMobileLanguages,
    'ru',
  ];

  const appStoreLocale =
    locale && locale !== 'en' && supportedAppleMobileLanguages.includes(locale)
      ? encodeURI(
          `/${(APPLE_LOCALE_MAPPING as localeCodeMapping)[locale] || locale}`
        )
      : '';

  const playStoreLocale =
    locale && locale !== 'en' && supportedGoogleMobileLanguages.includes(locale)
      ? `%3Fhl%3D${
          (GOOGLE_LOCALE_MAPPING as localeCodeMapping)[locale] || locale
        }`
      : '';

  const APP_STORE_URL = `${ADJUST_URL}/dyvodl2?redirect=https%3A%2F%2Fapps.apple.com${appStoreLocale}%2Fapp%2Fapple-store%2Fid1056182234`;
  const PLAY_STORE_URL = `${ADJUST_URL}/ju55aik?redirect=https%3A%2F%2Fplay.google.com%2Fstore%2Fapps%2Fdetails%3Fid%3Dfm.anchor.android${playStoreLocale}`;

  const mobileButtons = (
    <AppStoreDownloadButton
      white
      width={162}
      clickEventLocation={clickEventLocation}
      clickEventSection={clickEventSection}
      appStoreUrl={MOBILE_APP_STORE_URL || APP_STORE_URL}
      playStoreUrl={PLAY_STORE_URL || MOBILE_PLAY_STORE_URL}
    />
  );
  const desktopButtons = (
    <DesktopContainer>
      <PlatformBadge
        url={APP_STORE_URL}
        platformName="apple"
        clickEventLocation={clickEventLocation}
        clickEventSection={clickEventSection}
      />
      <PlatformBadge
        url={PLAY_STORE_URL}
        platformName="google"
        clickEventLocation={clickEventLocation}
        clickEventSection={clickEventSection}
      />
    </DesktopContainer>
  );
  const isMobile =
    serverRenderingUtils.isIOS() || serverRenderingUtils.isAndroidChrome();

  if (isRenderForMobileOnly && !isMobile) {
    return null;
  }

  return (
    <AppButtonContainer className={className}>
      {isMobile ? mobileButtons : desktopButtons}
    </AppButtonContainer>
  );
};
